import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { API } from "@/constants/roles";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="relative flex min-h-screen items-center justify-center px-4">
      <div className="bg-grid-cyan bg-grid-fade absolute inset-0" aria-hidden="true" />
      <div className="relative w-full max-w-md">
        <Link data-testid="forgot-back-login-link" to="/login" className="mb-6 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-cyan-600">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to login
        </Link>
        <div className="rounded-3xl border border-slate-200/80 bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10">
          {sent ? (
            <div className="text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </span>
              <h1 data-testid="forgot-sent-heading" className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-900">
                Check your inbox
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                If an account exists for <span className="font-semibold text-slate-900">{email}</span>, a password
                reset link is on its way. It expires in 1 hour.
              </p>
              <Link data-testid="forgot-sent-login-link" to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-600 hover:text-cyan-700">
                Back to login
              </Link>
            </div>
          ) : (
            <form data-testid="forgot-password-form" onSubmit={handleSubmit}>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-500">
                <Mail className="h-5 w-5 text-white" />
              </span>
              <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900">Forgot password?</h1>
              <p className="mt-2 text-sm text-slate-600">
                Enter your account email and we'll send you a secure reset link.
              </p>
              <div className="mt-8">
                <label htmlFor="forgot-email" className="mb-2 block text-xs font-semibold text-slate-700">
                  Email
                </label>
                <input
                  id="forgot-email"
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all"
                />
              </div>
              <button
                data-testid="forgot-submit-btn"
                type="submit"
                disabled={loading}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-cyan-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/25 transition-all duration-200 hover:bg-cyan-600 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Sending..." : "Send reset link"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
